import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import api from '../lib/api';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (user) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000); // Rafraîchir toutes les 30s
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const styles = {
    navbar: {
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #e5e7eb',
      padding: '16px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    left: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer'
    },
    logo: {
      fontSize: '24px'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#111827'
    },
    right: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px'
    },
    bellButton: {
      position: 'relative',
      background: 'none',
      border: 'none',
      fontSize: '20px',
      cursor: 'pointer',
      padding: '8px'
    },
    badge: {
      position: 'absolute',
      top: '4px',
      right: '4px',
      backgroundColor: '#ef4444',
      color: '#ffffff',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: '600',
      padding: '2px 6px',
      minWidth: '18px',
      textAlign: 'center'
    },
    userSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      backgroundColor: '#111827',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: '600'
    },
    userName: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    },
    roleBadge: {
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: '#f3f4f6',
      color: '#6b7280'
    },
    logoutButton: {
      padding: '8px 16px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      cursor: 'pointer'
    },
    notificationsPanel: {
      position: 'absolute',
      top: '60px',
      right: '24px',
      width: '400px',
      maxHeight: '500px',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 1000,
      overflowY: 'auto'
    },
    notificationItem: {
      padding: '16px',
      borderBottom: '1px solid #e5e7eb',
      cursor: 'pointer'
    },
    notificationUnread: {
      backgroundColor: '#f0f9ff'
    },
    notificationTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#111827',
      marginBottom: '4px'
    },
    notificationMessage: {
      fontSize: '13px',
      color: '#6b7280'
    },
    notificationTime: {
      fontSize: '12px',
      color: '#9ca3af',
      marginTop: '4px'
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleLabel = (role) => {
    return role === 'auditor' 
      ? (lang === 'fr' ? 'Auditeur' : 'Auditor')
      : 'Sponsor';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return lang === 'fr' ? 'À l\'instant' : 'Just now';
    if (diffMins < 60) return `${diffMins} ${lang === 'fr' ? 'min' : 'min'}`;
    if (diffHours < 24) return `${diffHours} ${lang === 'fr' ? 'h' : 'h'}`;
    if (diffDays < 7) return `${diffDays} ${lang === 'fr' ? 'j' : 'd'}`;
    return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US');
  };

  if (!user) return null;

  return (
    <div style={styles.navbar}>
      <div style={styles.left} onClick={() => navigate('/')}>
        <div style={styles.logo}>🛡️</div>
        <div style={styles.title}>AuditPlan</div>
      </div>

      <div style={styles.right}>
        <LanguageSwitcher />
        
        <button style={styles.bellButton} onClick={() => setShowNotifications(!showNotifications)}>
          🔔
          {unreadCount > 0 && <span style={styles.badge}>{unreadCount}</span>}
        </button>

        <div style={styles.userSection}>
          <div style={styles.avatar}>{getInitials(user.name)}</div>
          <div>
            <div style={styles.userName}>{user.name}</div>
            <div style={styles.roleBadge}>{getRoleLabel(user.role)}</div>
          </div>
        </div>

        <button style={styles.logoutButton} onClick={logout}>
          {lang === 'fr' ? 'Déconnexion' : 'Logout'}
        </button>
      </div>

      {showNotifications && (
        <>
          <div 
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
            onClick={() => setShowNotifications(false)}
          />
          <div style={styles.notificationsPanel}>
            {notifications.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
                {lang === 'fr' ? 'Aucune notification' : 'No notifications'}
              </div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  style={{
                    ...styles.notificationItem,
                    ...(notif.read ? {} : styles.notificationUnread)
                  }}
                  onClick={() => {
                    markAsRead(notif.id);
                    setShowNotifications(false);
                    if (user.role === 'auditor') {
                      navigate('/auditor');
                    } else {
                      navigate('/sponsor');
                    }
                  }}
                >
                  <div style={styles.notificationTitle}>{notif.title}</div>
                  <div style={styles.notificationMessage}>{notif.message}</div>
                  <div style={styles.notificationTime}>{formatTime(notif.created_at)}</div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
